/**
 * Qué conceptos NO debe ofrecer el selector de conceptos libres del finiquito.
 *
 * Vive aparte porque las DOS pestañas lo necesitan y tienen que coincidir: la 1 ("Finiquito",
 * el renglón de alta de conceptos libres) y la 2 ("Percepciones y Deducciones", la fila de alta
 * de la tabla del recibo). Si cada una decidiera por su cuenta, un concepto se podría agregar
 * desde una y no desde la otra.
 *
 * ── LA REGLA ─────────────────────────────────────────────────────────────────
 * Sólo se esconden tres cosas, y cada una por un motivo distinto:
 *
 *  1. **Los nueve conceptos del finiquito y el I.S.R. de finiquito.** `normalizarExtras`
 *     (finiquito.controllers.js) los RECHAZA: entran por su propio carril —las casillas y los
 *     días de la pestaña 1— y si además entraran como conceptos libres, dos mecanismos se
 *     pelearían por el mismo renglón. Ofrecerlos sólo serviría para producir un error.
 *  2. **Los que ya se agregaron como libres** en esta captura: `agregarExtra` los rechaza con
 *     un aviso, así que tampoco hay para qué ofrecerlos.
 *  3. **Los conceptos de la línea que YA SE VEN en el recibo.** Ésos no se agregan: se les edita
 *     el importe en la celda azul de la pestaña 2, que es el mismo mecanismo con otro nombre.
 *
 * ── LO QUE NO SE ESCONDE, Y POR QUÉ ES EL PUNTO ──────────────────────────────
 * Un concepto de la línea **en cero** —del catálogo del empleado, que este periodo no pagó nada—
 * SÍ se ofrece. Antes se excluía toda la línea sin mirar el importe, y como la pestaña 2 oculta
 * los renglones en cero (`conceptoVisibleEnRecibo`), esos conceptos quedaban **inalcanzables por
 * los dos lados**: ni se veían para editarles el importe, ni el selector los dejaba agregar.
 * Ése era el bug: conceptos que no tienen nada que ver con el finiquito y que el usuario sí
 * necesita capturar (un bono pactado, un descuento convenido) simplemente no aparecían.
 *
 * Agregarlos es seguro: un concepto libre entra con `importeTotalManual` + `calcularInverso`, o
 * sea que el importe capturado REEMPLAZA la fórmula en vez de sumarse a ella. Sobre un renglón
 * que valía cero el resultado es exactamente el mismo que forzarle el importe a mano.
 */

const esManual = (v) => v !== null && v !== undefined;

/**
 * ¿Este renglón de `conceptosLinea` se ve en el recibo?
 *
 * Un renglón en cero se oculta —son los conceptos del catálogo que no aplicaron—, pero NUNCA uno
 * que el usuario tocó: si se fuerza un importe a 0 el renglón tiene que seguir a la vista, o no
 * habría forma de quitarle el manual.
 */
export const conceptoVisibleEnRecibo = (c) =>
  !!(c.importe || esManual(c.importeTotalManual) || c.agregadoManual);

/**
 * Los ids que el selector de conceptos libres no debe ofrecer.
 *
 * @param {Array}  filas         `preview.filas` — los nueve conceptos del finiquito
 * @param {Array}  conceptosLinea `preview.conceptosLinea` — todos los renglones de la línea
 * @param {Array}  extras        los conceptos libres ya capturados
 * @param {String} isrFiniquitoConceptoId  `preview.isrFiniquitoConceptoId`
 * @returns {Set<String>}
 */
export function idsNoOfrecibles({ filas, conceptosLinea, extras, isrFiniquitoConceptoId }) {
  const ids = new Set();

  const agregar = (v) => {
    if (v === null || v === undefined || v === "") return;
    ids.add(String(v));
  };

  // 1. Los nueve del finiquito + el ISR de finiquito: el backend los rechaza.
  (filas || []).forEach((f) => agregar(f.conceptoNominaId));
  agregar(isrFiniquitoConceptoId);

  // 2. Los libres ya capturados.
  (extras || []).forEach((e) => agregar(e.conceptoNominaId));

  // 3. Sólo los renglones de la línea que de verdad se ven —y por lo tanto se pueden editar—.
  (conceptosLinea || [])
    .filter(conceptoVisibleEnRecibo)
    .forEach((c) => agregar(c.conceptoNominaId));

  return ids;
}
